<template>
  <section class="about-section">
    <!-- Hero Section -->
    <div class="about-hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            {{ tr('about.title') }}
          </h1>
          <div class="hero-decoration">
            <div class="music-note">♪</div>
            <div class="music-note">♫</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Timeline Section -->
    <div class="timeline-section section">
      <div class="container">
        <div class="timeline-horizontal">
          <div class="timeline-item" v-for="(item, index) in timelineItems" :key="index">
            <div class="timeline-marker">
              <i :class="item.icon"></i>
            </div>
            <div class="timeline-content">
              <h3 class="timeline-year">{{ item.year }}</h3>
              <p class="timeline-text">{{ tr(item.textKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Historia + Galería -->
    <div class="history-content section">
      <div class="container">
        <div class="history-stack">
          <Card variant="elevated" padding="lg" class="gallery-card">
            <div class="gallery-header">
              <p class="gallery-eyebrow">{{ tr('about.galleryEyebrow') }}</p>
              <h2 class="gallery-title">{{ tr('about.galleryTitle') }}</h2>
              <p class="gallery-subtitle">{{ tr('about.gallerySubtitle') }}</p>
            </div>

            <div class="carousel-wrapper">
              <Carousel
                :itemsToShow="1"
                :autoplay="8000"
                :transition="600"
                :wrapAround="true"
                :mouseDrag="true"
                :touchDrag="true"
                :breakpoints="carouselBreakpoints"
                class="modern-carousel"
              >
                <template #slides>
                  <Slide v-for="slide in slides" :key="slide.id">
                    <div class="slide-content">
                      <img
                        :src="slide.image"
                        :alt="slide.altKey ? tr(slide.altKey) : tr('about.imageAltDefault')"
                        class="slide-image"
                        :class="{ 'logo-image': slide.isLogo }"
                        loading="lazy"
                        decoding="async"
                        :width="slide.width"
                        :height="slide.height"
                      />
                    </div>
                  </Slide>
                </template>

                <template #addons="{ slidesCount }">
                  <Navigation v-if="slidesCount > 1" />
                  <Pagination v-if="slidesCount > 1" />
                </template>
              </Carousel>
            </div>
          </Card>

          <div class="main-content">
          <div class="content-block">
            <h2 class="content-title">
              <i class="fas fa-history"></i>
              {{ tr('about.sectionBeginningsTitle') }}
            </h2>
            <p class="content-text">
              {{ tr('about.sectionBeginningsText') }}
            </p>
          </div>

          <div class="content-block">
            <h2 class="content-title">
              <i class="fas fa-globe-americas"></i>
              {{ tr('about.sectionInternationalTitle') }}
            </h2>
            <p class="content-text">
              {{ tr('about.sectionInternationalText') }}
            </p>
          </div>

          <div class="content-block">
            <h2 class="content-title">
              <i class="fas fa-trophy"></i>
              {{ tr('about.sectionHighlightsTitle') }}
            </h2>
            <p class="content-text">
              {{ tr('about.sectionHighlightsText') }}
            </p>
          </div>

          <div class="content-block">
            <h2 class="content-title">
              <i class="fas fa-star"></i>
              {{ tr('about.sectionRecognitionTitle') }}
            </h2>
            <p class="content-text">
              {{ tr('about.sectionRecognitionText') }}
            </p>
          </div>

          <div class="content-block">
            <h2 class="content-title">
              <i class="fas fa-rocket"></i>
              {{ tr('about.sectionFutureTitle') }}
            </h2>
            <p class="content-text">
              {{ tr('about.sectionFutureText') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- CTA Section -->
    <div class="cta-section section">
      <div class="container">
        <Card variant="elevated" padding="lg" class="cta-card">
          <div class="cta-content">
            <h2 class="cta-title">{{ tr('about.ctaTitle') }}</h2>
            <p class="cta-text">
              {{ tr('about.ctaText') }}
            </p>
            <router-link to="/contact" custom v-slot="{ navigate }">
              <Button variant="primary" size="lg" @click="navigate">
                <i class="fas fa-envelope"></i>
                {{ tr('about.ctaButton') }}
              </Button>
            </router-link>
          </div>
        </Card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue';
import { useHead } from '@vueuse/head';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel';
import Card from '../ui/Card.vue';
import Button from '../ui/Button.vue';
import { buildHeadConfig, getBreadcrumbJsonLd } from '../../seo/meta';
import logoNuevo from '../../assets/24_07_2025_LogoBandaNuevo.jpg';
import image2 from '../../assets/foto1.png';
import image3 from '../../assets/foto2.png';
import image4 from '../../assets/foto3.png';
import image5 from '../../assets/foto4.png';
import image6 from '../../assets/foto5.png';

// Access i18n
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

useHead(
  buildHeadConfig('about', {
    jsonLd: [getBreadcrumbJsonLd('about')],
  }),
)

const slides = [
  {
    id: 1,
    image: logoNuevo,
    isLogo: true,
    altKey: 'home.altLogo',
    width: 1200,
    height: 900,
  },
  {
    id: 2,
    image: image3,
    isLogo: false,
    altKey: 'home.altPerformance',
    width: 1600,
    height: 1067,
  },
  {
    id: 3,
    image: image2,
    isLogo: false,
    altKey: 'home.altConcert',
    width: 1600,
    height: 1067,
  },
  {
    id: 4,
    image: image4,
    isLogo: false,
    altKey: 'home.altEvent',
    width: 1600,
    height: 1067,
  },
  {
    id: 5,
    image: image5,
    isLogo: false,
    altKey: 'home.altPresentation',
    width: 1600,
    height: 1067,
  },
  {
    id: 6,
    image: image6,
    isLogo: false,
    altKey: 'home.altInAction',
    width: 1600,
    height: 1067,
  },
];

const carouselBreakpoints = {
  768: {
    itemsToShow: 2,
  },
  1024: {
    itemsToShow: 3,
  },
};

const timelineItems = [
  {
    year: '1880',
    textKey: 'about.timeline1880',
    icon: 'fas fa-flag'
  },
  {
    year: '1984',
    textKey: 'about.timeline1984',
    icon: 'fas fa-redo'
  },
  {
    year: '1989',
    textKey: 'about.timeline1989',
    icon: 'fas fa-plane'
  },
  {
    year: '2006',
    textKey: 'about.timeline2006',
    icon: 'fas fa-award'
  },
  {
    year: '2008',
    textKey: 'about.timeline2008',
    icon: 'fas fa-theater-masks'
  },
  {
    year: '2025',
    textKey: 'about.timeline2025',
    icon: 'fas fa-rocket'
  }
];
</script>

<style scoped>
/* About Section */
.about-section {
  background: white;
}

/* Hero Section */
.about-hero {
  position: relative;
  min-height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  overflow: hidden;
  padding: var(--spacing-16) var(--spacing-4);
}

.about-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.hero-content {
  position: relative;
  text-align: center;
  z-index: 1;
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  color: white;
  margin-bottom: var(--spacing-4);
  font-family: var(--font-family-serif);
}

.hero-decoration {
  display: flex;
  gap: var(--spacing-6);
  justify-content: center;
  margin-top: var(--spacing-6);
}

.music-note {
  font-size: 3rem;
  color: var(--color-secondary-400);
  animation: float 3s ease-in-out infinite;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
}

.music-note:nth-child(2) {
  animation-delay: 1.5s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

/* Timeline Section */
.timeline-section {
  padding: var(--spacing-20) 0;
  background: var(--gradient-primary);
  position: relative;
  overflow: hidden;
}

.timeline-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.timeline-horizontal {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-6);
  position: relative;
  z-index: 1;
}

@media (min-width: 768px) {
  .timeline-horizontal {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .timeline-horizontal {
    grid-template-columns: repeat(6, 1fr);
  }
}

/* History Content */
.history-content {
  padding: var(--spacing-20) 0 var(--spacing-24) 0;
  background: var(--color-neutral-50);
}
.history-stack {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-10);
  max-width: 1400px;
  margin: 0 auto;
}

@media (min-width: 1024px) {
  .history-stack {
    gap: var(--spacing-12);
  }
}

/* Galería */
.gallery-card {
  padding: 0;
  overflow: hidden;
}

.gallery-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  padding: var(--spacing-8) var(--spacing-6);
  text-align: center;
  color: white;
}

.gallery-eyebrow {
  display: inline-flex;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  margin: 0 0 var(--spacing-2) 0;
  line-height: 1.2;
  text-align: center;
  text-wrap: balance;
  color: rgba(255, 255, 255, 0.85);
}

.gallery-title {
  margin: 0;
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-serif);
  color: white;
}

.gallery-subtitle {
  margin: var(--spacing-3) 0 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--font-size-base);
}

.carousel-wrapper {
  position: relative;
  padding: var(--spacing-5);
  background: white;
}

.modern-carousel {
  overflow: hidden;
}

:deep(.carousel__slide) {
  padding: var(--spacing-2);
}

.slide-content {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: var(--color-neutral-100);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
}

.slide-image.logo-image {
  object-fit: contain;
  padding: var(--spacing-10);
  background: white;
}

.slide-content:hover .slide-image:not(.logo-image) {
  transform: scale(1.05);
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  background: var(--color-primary-600);
  color: white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  transition: all 0.3s ease;
}

:deep(.carousel__prev):hover,
:deep(.carousel__next):hover {
  background: var(--color-primary-700);
  transform: scale(1.1);
}

:deep(.carousel__pagination-button) {
  background: var(--color-neutral-300);
  transition: all 0.3s ease;
}

:deep(.carousel__pagination-button--active) {
  background: var(--color-primary-600);
  width: 32px;
  border-radius: 6px;
}

/* Escola */
.school-card {
  max-width: 1100px;
  margin: 0 auto;
  box-shadow: var(--shadow-2xl);
}

.school-header {
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  margin-bottom: var(--spacing-6);
}

.school-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--gradient-music);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: var(--shadow-lg);
}

.school-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  margin: 0 0 var(--spacing-1) 0;
  color: var(--color-primary-600);
}

.school-title {
  margin: 0;
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-serif);
  color: var(--color-primary-800);
}

.school-subtitle {
  margin: var(--spacing-2) 0 0 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
}

.school-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-6);
}

@media (min-width: 768px) {
  .school-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.school-block h3 {
  margin: 0 0 var(--spacing-2) 0;
  font-size: var(--font-size-lg);
  color: var(--color-primary-700);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.school-block p {
  margin: 0;
  color: var(--color-text-secondary);
  line-height: 1.6;
}

/* Timeline Items */
.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: white;
}

.timeline-marker {
  width: 56px;
  height: 56px;
  background: var(--color-secondary-500);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: var(--shadow-xl);
  margin-bottom: var(--spacing-3);
  transition: transform 0.3s ease;
}

.timeline-item:hover .timeline-marker {
  transform: scale(1.1);
}

.timeline-content {
  width: 100%;
}

.timeline-year {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--spacing-2);
  color: var(--color-secondary-200);
  font-family: var(--font-family-serif);
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
}

.timeline-text {
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.98);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.4);
}

@media (max-width: 767px) {
  .timeline-marker {
    width: 48px;
    height: 48px;
    font-size: 1.2rem;
  }
  
  .timeline-year {
    font-size: var(--font-size-xl);
  }
  
  .timeline-text {
    font-size: var(--font-size-xs);
  }
}

/* Main Content */
.main-content {
  background: white;
  padding: var(--spacing-10);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
}

@media (min-width: 1024px) {
  .main-content {
    padding: var(--spacing-12);
  }
}

.content-block {
  margin-bottom: var(--spacing-16);
  padding-bottom: var(--spacing-12);
  border-bottom: 2px solid var(--color-neutral-100);
}

.content-block:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.content-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin-bottom: var(--spacing-4);
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  font-family: var(--font-family-serif);
}

.content-title i {
  color: var(--color-secondary-500);
  font-size: 1.5rem;
}

.content-text {
  font-size: var(--font-size-base);
  line-height: 1.8;
  color: var(--color-text-secondary);
}

/* CTA Section */
.cta-section {
  padding: var(--spacing-20) 0;
  background: linear-gradient(180deg, rgba(79, 148, 160, 0.08) 0%, var(--color-neutral-50) 100%);
  position: relative;
  overflow: hidden;
}

.cta-card {
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  box-shadow: var(--shadow-2xl);
}

.cta-content {
  text-align: center;
  padding: var(--spacing-4);
}

.cta-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-700);
  margin-bottom: var(--spacing-6);
  font-family: var(--font-family-serif);
  line-height: 1.2;
}

.cta-text {
  font-size: var(--font-size-xl);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-8);
  line-height: 1.8;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  font-weight: var(--font-weight-normal);
}

/* Responsive */
@media (max-width: 768px) {
  .about-hero {
    min-height: 30vh;
  }

  .timeline-side {
    padding: var(--spacing-6);
  }

  .main-content {
    padding: var(--spacing-10);
  }

  .content-title {
    font-size: var(--font-size-xl);
  }

  .music-note {
    font-size: 2rem;
  }
}

/* Refresh 2026 - visual coherence with Home */
.about-section {
  background: transparent;
}

.about-hero {
  min-height: 42vh;
  padding: clamp(2.8rem, 6vw, 5rem) var(--spacing-4);
  background:
    radial-gradient(circle at 80% 14%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.about-hero::before {
  background:
    radial-gradient(circle at 18% 85%, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 40%);
}

.hero-title {
  letter-spacing: var(--letter-spacing-tight);
  text-wrap: balance;
}

.timeline-section {
  background: linear-gradient(180deg, rgba(16, 41, 59, 0.9) 0%, rgba(20, 56, 78, 0.92) 100%);
}

.timeline-marker {
  background: linear-gradient(135deg, var(--color-secondary-400), var(--color-secondary-600));
}

.history-content {
  background: transparent;
}

.gallery-card,
.school-card,
.main-content,
.cta-card {
  background: var(--surface-card-strong);
  border: 1px solid rgba(16, 41, 59, 0.12);
  box-shadow: var(--shadow-xl);
}

.gallery-header {
  background: linear-gradient(132deg, #132f44 0%, #224e6d 54%, #8d6228 100%);
}

.carousel-wrapper {
  background: transparent;
}

.cta-section {
  background: none;
}

.cta-card {
  background:
    radial-gradient(circle at 15% 14%, rgba(219, 174, 80, 0.18), rgba(219, 174, 80, 0) 45%),
    linear-gradient(140deg, #0f293b 0%, #173c56 56%, #1f584d 100%);
  border-color: rgba(255, 255, 255, 0.14);
}

.cta-title,
.cta-text {
  color: #f8f5ef;
}
</style>
